// Phase 4D.3A — browser-native voice layer (pure, testable core).
//
// No paid TTS/STT service. Nothing here records anything: it only reports what
// the current browser can do and how to speak an already-sanitised answer.
//
// Invariants:
//   * No auto-recording and no background listening — the caller must start
//     from an explicit user gesture.
//   * Voice speaks the SAME sanitised assistant text the screen shows.
//   * Voice never bypasses the action confirmation gate: a spoken confirmation
//     is not a confirmation; the visible control still has to be pressed.

export interface VoiceSupport {
  recognition: boolean;
  synthesis: boolean;
  /** True only when the mic path is usable. */
  supported: boolean;
  /** Honest, user-facing explanation when something is missing. */
  fallbackMessage: string | null;
}

type MaybeWindow =
  | {
      SpeechRecognition?: unknown;
      webkitSpeechRecognition?: unknown;
      speechSynthesis?: unknown;
      isSecureContext?: boolean;
    }
  | null
  | undefined;

export function detectVoiceSupport(win: MaybeWindow): VoiceSupport {
  const w = win ?? null;
  const secure = w ? w.isSecureContext !== false : false;
  const recognition = Boolean(w && (w.SpeechRecognition || w.webkitSpeechRecognition) && secure);
  const synthesis = Boolean(w && w.speechSynthesis && secure);
  const supported = recognition;

  let fallbackMessage: string | null = null;
  if (!w) fallbackMessage = "Voice is only available in the browser.";
  else if (!secure) fallbackMessage = "Voice needs a secure (https) connection.";
  else if (!recognition && !synthesis) {
    fallbackMessage = "This browser does not support voice. Type your question instead.";
  } else if (!recognition) {
    fallbackMessage = "This browser cannot listen. You can still have answers read aloud.";
  } else if (!synthesis) {
    fallbackMessage = "This browser cannot read answers aloud. Dictation still works.";
  }

  return { recognition, synthesis, supported, fallbackMessage };
}

export type MicState = "idle" | "listening" | "denied" | "unsupported" | "error";

/** Visible label for the current mic state. Never silent about listening. */
export function micStateLabel(state: MicState): string {
  switch (state) {
    case "listening":
      return "Listening — tap to stop";
    case "denied":
      return "Microphone blocked in your browser settings";
    case "unsupported":
      return "Voice input unavailable in this browser";
    case "error":
      return "Voice input stopped unexpectedly";
    default:
      return "Speak your question";
  }
}

/** Strip markdown, code and URLs so speech matches the readable answer. */
export function sanitizeForSpeech(text: string, maxChars = 1200): string {
  const out = String(text ?? "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/^#{1,6}\s*/gm, "")
    .replace(/[*_>#|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return out.length > maxChars ? `${out.slice(0, maxChars).trimEnd()}…` : out;
}

/**
 * Voice never auto-confirms an action. A spoken "yes" is treated as ordinary
 * text; the confirmation control still has to be pressed on screen.
 */
export const VOICE_CONFIRMS_ACTIONS = false;
